import { useEffect, useState } from "react";
import { Users as UsersIcon, Plus, X, Loader2, AlertCircle, KeyRound, Trash2, Power, PowerOff } from "lucide-react";
import { PageHeader } from "../components/Layout";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";

const ROLES = [
  { v: "admin",           l: "Admin — full access (god-mode)" },
  { v: "ceo",             l: "CEO — read-only across all modules, export reports" },
  { v: "general_manager", l: "General Manager — ops + rentals oversight (no gate creation)" },
  { v: "depot_manager",   l: "Depot Manager — terminal ops only (no rentals)" },
  { v: "finance",         l: "Finance — invoices · tariffs · billing emails" },
  { v: "supervisor",      l: "Supervisor — approves operator work, certifies washes" },
  { v: "operator",        l: "Operator — input-only (gate IN/OUT, washes, EIRs)" },
];

const ROLE_PILL = {
  admin:           "ps-pill ps-pill-error",
  ceo:             "ps-pill ps-pill-info",
  general_manager: "ps-pill ps-pill-info",
  depot_manager:   "ps-pill ps-pill-info",
  finance:         "ps-pill ps-pill-warning",
  supervisor:      "ps-pill ps-pill-warning",
  operator:        "ps-pill ps-pill-neutral",
};

function RolePill({ role }) {
  return <span className={ROLE_PILL[role] || "ps-pill ps-pill-neutral"}><span className="dot" />{role.replace("_", " ")}</span>;
}

function ActivePill({ active }) {
  return active === false
    ? <span className="ps-pill ps-pill-error"><span className="dot" />Disabled</span>
    : <span className="ps-pill ps-pill-success"><span className="dot" />Active</span>;
}

function CreateUserDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({ email: "", name: "", password: "", role: "operator", active: true });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => { if (open) { setForm({ email: "", name: "", password: "", role: "operator", active: true }); setErr(""); } }, [open]);
  if (!open) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/users", form);
      toast.success(`User ${data.email} created`);
      onCreated?.(data);
      onClose?.();
    } catch (e2) { setErr(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center p-4 overflow-y-auto" data-testid="create-user-dialog">
      <div className="bg-white max-w-md w-full mt-16 rounded-sm shadow-xl">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-gray-500">New record</div>
            <h2 className="font-display text-lg font-extrabold">Add user</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-900"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-3">
          {err && (
            <div className="border border-red-200 bg-red-50 text-red-900 text-sm p-3 flex items-start gap-2" data-testid="create-user-error">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /><div>{err}</div>
            </div>
          )}
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Email *</label>
            <input type="email" required value={form.email} onChange={(e) => set("email", e.target.value.toLowerCase().trim())} className="ps-input font-mono" data-testid="create-user-email" />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Full name *</label>
            <input required value={form.name} onChange={(e) => set("name", e.target.value)} className="ps-input" data-testid="create-user-name" />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Temporary password *</label>
            <input type="password" required minLength={8} value={form.password} onChange={(e) => set("password", e.target.value)} className="ps-input font-mono" placeholder="Min. 8 characters" data-testid="create-user-password" />
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase tracking-[0.16em] text-gray-600 mb-1">Role *</label>
            <select value={form.role} onChange={(e) => set("role", e.target.value)} className="ps-input font-mono" data-testid="create-user-role">
              {ROLES.map((r) => <option key={r.v} value={r.v}>{r.l}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-2 pt-1">
            <input type="checkbox" id="user-active" checked={form.active} onChange={(e) => set("active", e.target.checked)} data-testid="create-user-active" />
            <label htmlFor="user-active" className="text-sm">Account active</label>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="ps-btn ps-btn-secondary">Cancel</button>
            <button type="submit" disabled={busy} className="ps-btn ps-btn-primary" data-testid="submit-create-user">
              {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating…</> : "Create user"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ResetPasswordDialog({ target, onClose }) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  if (!target) return null;
  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setBusy(true);
    try {
      await api.post(`/users/${target.id}/password`, { password });
      toast.success(`Password reset for ${target.email}`);
      onClose?.();
    } catch (e2) { setErr(formatApiError(e2)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-start justify-center p-4" data-testid="reset-password-dialog">
      <div className="bg-white max-w-md w-full mt-24 rounded-sm shadow-xl">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="font-display text-lg font-extrabold">Reset password — {target.email}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-900"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-3">
          {err && <div className="border border-red-200 bg-red-50 text-red-900 text-sm p-3">{err}</div>}
          <input type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} className="ps-input font-mono" placeholder="New password (min 8 chars)" autoFocus data-testid="reset-password-input" />
          <div className="text-xs text-gray-500">The user will need to sign in again with this new password.</div>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={onClose} className="ps-btn ps-btn-secondary">Cancel</button>
            <button type="submit" disabled={busy} className="ps-btn ps-btn-primary" data-testid="submit-reset-password">
              {busy ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : "Reset password"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function Users() {
  const { user: me } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [resetting, setResetting] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setItems(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const updateUser = async (u, patch) => {
    try {
      await api.patch(`/users/${u.id}`, patch);
      toast.success(`${u.email} updated`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteUser = async (u) => {
    if (!window.confirm(`Delete ${u.email}? This cannot be undone.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(`${u.email} deleted`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="users-page">
      <PageHeader
        title="Users"
        subtitle="Manage operator, supervisor and admin accounts."
        testid="users-header"
        actions={
          <button className="ps-btn ps-btn-primary" onClick={() => setShowCreate(true)} data-testid="new-user">
            <Plus className="w-4 h-4" /> New user
          </button>
        }
      />
      <div className="p-6 sm:p-8">
        <div className="border border-gray-200 bg-white overflow-x-auto">
          <table className="ps-table" data-testid="users-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Name</th>
                <th>Role</th>
                <th>Depots</th>
                <th>Status</th>
                <th>Created</th>
                <th>Last login</th>
                <th className="text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="text-center py-10 text-gray-400">Loading…</td></tr>}
              {!loading && items.length === 0 && (
                <tr><td colSpan={8} className="text-center py-10 text-gray-400">No users.</td></tr>
              )}
              {!loading && items.map((u) => {
                const isMe = u.id === me?.id;
                return (
                  <tr key={u.id} data-testid={`user-row-${u.email}`}>
                    <td className="font-mono font-semibold">
                      {u.email} {isMe && <span className="ps-pill ps-pill-success ml-2"><span className="dot" />you</span>}
                    </td>
                    <td>{u.name}</td>
                    <td>
                      <select
                        value={u.role}
                        disabled={isMe}
                        onChange={(e) => updateUser(u, { role: e.target.value })}
                        className="ps-input font-mono h-8 text-xs w-36"
                        data-testid={`role-${u.email}`}
                      >
                        {ROLES.map((r) => <option key={r.v} value={r.v}>{r.v}</option>)}
                      </select>
                    </td>
                    <td>
                      <DepotChips
                        user={u}
                        onChange={(codes) => updateUser(u, { allowed_depots: codes })}
                      />
                    </td>
                    <td><ActivePill active={u.active} /></td>
                    <td className="text-xs text-gray-500 font-mono">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                    <td className="text-xs text-gray-500 font-mono">{u.last_login_at ? new Date(u.last_login_at).toLocaleString() : "—"}</td>
                    <td className="text-right">
                      <div className="inline-flex gap-1">
                        <button
                          className="ps-btn ps-btn-secondary h-7 px-2 text-xs"
                          onClick={() => setResetting(u)}
                          data-testid={`reset-${u.email}`}
                        ><KeyRound className="w-3.5 h-3.5" /> Reset PW</button>
                        <button
                          className="ps-btn ps-btn-secondary h-7 px-2 text-xs"
                          disabled={isMe}
                          onClick={() => updateUser(u, { active: !u.active })}
                          data-testid={`toggle-${u.email}`}
                        >
                          {u.active === false
                            ? <><Power className="w-3.5 h-3.5" /> Enable</>
                            : <><PowerOff className="w-3.5 h-3.5" /> Disable</>}
                        </button>
                        <button
                          className="ps-btn ps-btn-secondary h-7 px-2 text-xs text-red-600"
                          disabled={isMe}
                          onClick={() => deleteUser(u)}
                          data-testid={`delete-${u.email}`}
                        ><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      <CreateUserDialog open={showCreate} onClose={() => setShowCreate(false)} onCreated={load} />
      <ResetPasswordDialog target={resetting} onClose={() => setResetting(null)} />
    </div>
  );
}


// ────────────────────────────────────────────────────────────────────────
// Depot chips — compact per-user depot allow-list editor. Admins always
// show all three chips lit (role check short-circuits on the backend);
// everyone else sees "No depots → user can't see anything" prominently
// when the list is empty, so new accounts get provisioned immediately.
const DEPOT_CHIPS = [
  { code: "GH-TEM", flag: "\u{1F1EC}\u{1F1ED}" },
  { code: "TG-LFW", flag: "\u{1F1F9}\u{1F1EC}" },
  { code: "GA-LBV", flag: "\u{1F1EC}\u{1F1E6}" },
];

function DepotChips({ user, onChange }) {
  const allowed = user.allowed_depots || [];
  const isAdmin = user.role === "admin";
  // Admins implicitly see everything — show the chips as locked-on so the
  // UI matches backend behaviour without needing a redundant row of toggles.
  if (isAdmin) {
    return (
      <div className="flex flex-wrap gap-1" data-testid={`depots-${user.email}`}>
        {DEPOT_CHIPS.map((d) => (
          <span key={d.code}
            className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-gray-300 bg-gray-50 text-[10px] font-mono text-gray-600"
            title="Admins see every depot"
          >
            <span className="text-xs leading-none">{d.flag}</span>{d.code}
          </span>
        ))}
      </div>
    );
  }
  const toggle = (code) => {
    const set = new Set(allowed);
    if (set.has(code)) set.delete(code); else set.add(code);
    onChange(Array.from(set));
  };
  return (
    <div className="flex flex-wrap gap-1" data-testid={`depots-${user.email}`}>
      {DEPOT_CHIPS.map((d) => {
        const on = allowed.includes(d.code);
        return (
          <button
            key={d.code}
            type="button"
            onClick={() => toggle(d.code)}
            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-[10px] font-mono transition-colors ${on ? "border-[#99D420] bg-[#99D420]/20 text-[#1A1410] font-bold" : "border-gray-300 bg-white text-gray-400 hover:text-gray-700"}`}
            data-testid={`depot-toggle-${user.email}-${d.code}`}
            title={on ? `Revoke ${d.code}` : `Grant ${d.code}`}
          >
            <span className="text-xs leading-none">{d.flag}</span>{d.code}
          </button>
        );
      })}
      {allowed.length === 0 && (
        <span className="ml-1 text-[10px] text-amber-700 font-mono" data-testid={`depots-empty-${user.email}`}>
          · locked out
        </span>
      )}
    </div>
  );
}
